import React from 'react'
import { isNull } from 'lodash'
import {
  Invoices_getInvoices_pagination,
  SortDirectionEnum,
  InsuranceErrors_getInsuranceErrors_insurances,
} from '@modmd/data'
import { Box } from 'components/Layout'
import Datalist, { DataListProps } from 'components/Datalist'
import Pagination from 'components/Pagination'
import Card from 'components/Card'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import { DEVICE } from 'theme'
import { useHistory } from 'react-router'
import { ROUTES } from 'internal-portal/constants/routes'

interface ColumnLabels {
  id?: string
  patientLastName?: string
  patientFirstName?: string
  patientDOB?: string
  insuranceCompany?: string
  subscriber?: string
  subscriberDOB?: string
  insurance?: string
  insurancePlan?: string
  gender?: string
  ssn?: string
  govId?: string
  eligibilityError?: string
  lastEligibilityCheck?: string
}

interface Props {
  tableHeader?: React.ReactElement
  tableFooter?: React.ReactElement
  insuranceErrors: InsuranceErrors_getInsuranceErrors_insurances[]
  sorter?: DataListProps['sorter']
  columnLabels?: ColumnLabels
  pagination?: Invoices_getInvoices_pagination
  isCard?: boolean
  isLoading?: boolean
  hasPaginationInFooter?: boolean
  onPageChange?: (page: number) => void
  onSortChange?: (sortBy: string, direction: SortDirectionEnum) => void
  pageLength?: number
  testSessionRefetch?: () => void
}

export const PAGE_LENGTH = 10

const defaultColumnLabels = {
  id: 'Id',
  patientLastName: 'Patient Last Name',
  patientFirstName: 'Patient First Name',
  patientDOB: 'Patient DOB',
  insuranceCompany: 'Insurance Company',
  subscriber: 'Subscriber',
  subscriberDOB: 'Subscriber DOB',
  insurance: 'Insurance',
  insurancePlan: 'Insurance Plan',
  gender: 'Gender',
  ssn: 'SSN',
  govId: 'Gov. Issued Id',
  eligibilityError: 'Error Information',
  lastEligibilityCheck: 'Last Eligibility Check',
}

export const InsuranceErrorsDataList: React.VFC<Props> = ({
  tableHeader,
  tableFooter,
  insuranceErrors,
  sorter,
  pagination,
  columnLabels: providedColumnLabels,
  isCard = true,
  isLoading,
  hasPaginationInFooter = false,
  onSortChange,
  onPageChange,
  pageLength = PAGE_LENGTH,
}) => {
  const history = useHistory()
  const isDesktop = useIsMinDevice(DEVICE.DESKTOP)
  const isTablet = useIsMinDevice(DEVICE.TABLET)
  const columnLabels = { ...defaultColumnLabels, ...(providedColumnLabels || {}) }
  const numOfColumns = Object.values(columnLabels).filter((label) => label !== null).length
  const rowGridTemplateColumns = '1fr 1fr 1fr 3fr 2fr 2fr 3fr 1fr 1fr'
  const paginationComp = pagination && pagination.total > pageLength && onPageChange && (
    <Pagination
      firstPage={1}
      lastPage={Math.ceil(pagination.total / pageLength) || 1}
      currentPage={Math.ceil(pagination.from / pageLength) + 1}
      onPageChange={onPageChange}
    />
  )

  const Wrapper = isCard ? Card : Box
  return (
    <>
      <Wrapper>
        <Datalist
          isLoading={isLoading}
          skeletonProps={{
            columns: numOfColumns,
            rows: pageLength,
          }}
          data={insuranceErrors}
          sorter={sorter}
          onSortChange={onSortChange}
          header={tableHeader}
          footer={hasPaginationInFooter && paginationComp ? paginationComp : tableFooter}
          shouldRenderTableHeader={isDesktop}
          showActionIndicators
          renderTableHeader={
            isDesktop
              ? () => (
                  <Datalist.Row gridTemplateColumns={rowGridTemplateColumns}>
                    {!isNull(columnLabels.id) ? (
                      <Datalist.HeaderCell label={columnLabels.id} />
                    ) : null}
                    {!isNull(columnLabels.patientLastName) ? (
                      <Datalist.HeaderCell label={columnLabels.patientLastName} />
                    ) : null}
                    {!isNull(columnLabels.patientFirstName) ? (
                      <Datalist.HeaderCell label={columnLabels.patientFirstName} />
                    ) : null}
                    {!isNull(columnLabels.insuranceCompany) ? (
                      <Datalist.HeaderCell label={columnLabels.insuranceCompany} />
                    ) : null}
                    {!isNull(columnLabels.insurancePlan) ? (
                      <Datalist.HeaderCell label={columnLabels.insurancePlan} />
                    ) : null}
                    {!isNull(columnLabels.subscriber) ? (
                      <Datalist.HeaderCell label={columnLabels.subscriber} />
                    ) : null}
                    {!isNull(columnLabels.eligibilityError) ? (
                      <Datalist.HeaderCell label={columnLabels.eligibilityError} />
                    ) : null}
                    {!isNull(columnLabels.lastEligibilityCheck) ? (
                      <Datalist.HeaderCell label={columnLabels.lastEligibilityCheck} />
                    ) : null}
                  </Datalist.Row>
                )
              : undefined
          }
          renderTableRow={(data) => {
            const { userId } = data

            return (
              <Datalist.Row
                gridTemplateColumns={{
                  _: 'repeat(2, 1fr)',
                  DESKTOP: rowGridTemplateColumns,
                }}
                onClick={() => {
                  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                  history.push(`/${ROUTES.USERS}/${String(userId)}`)
                }}
                {...(!isTablet ? { alignItems: 'start' } : {})}
              >
                <Datalist.Cell label={columnLabels.id}>{data.id}</Datalist.Cell>
                <Datalist.Cell label={columnLabels.patientLastName}>
                  {data.User.lastName}
                </Datalist.Cell>
                <Datalist.Cell label={columnLabels.patientFirstName}>
                  {data.User.firstName}
                </Datalist.Cell>
                <Datalist.Cell label={columnLabels.insuranceCompany}>
                  {data.insuranceCompany}
                </Datalist.Cell>
                <Datalist.Cell label={columnLabels.insurancePlan}>{data.plan}</Datalist.Cell>
                <Datalist.Cell label={columnLabels.subscriber}>{`${
                  data.subscriberFirstName || ''
                } ${data.subscriberLastName || ''}`}</Datalist.Cell>
                <Datalist.Cell label={columnLabels.eligibilityError}>
                  {data.eligibilityErrorMessage}
                </Datalist.Cell>
                <Datalist.Cell label={columnLabels.lastEligibilityCheck}>
                  {new Date(data.lastEligibilityCheck).toLocaleDateString()}
                </Datalist.Cell>
              </Datalist.Row>
            )
          }}
        />
      </Wrapper>
      {!hasPaginationInFooter && paginationComp && (
        <Pagination.Wrapper>{paginationComp}</Pagination.Wrapper>
      )}
    </>
  )
}
